import React, { useEffect, useState } from "react";
import Loader from "../components/Loader";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import perfumeriesData from "../data/perfumeries.json";
import parse from "html-react-parser";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../components/Breadcrumbs";
import GalleryMini from "../components/GalleryMini";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_LOGO, SITE_NAME, SITE_URL } from "../seo/site";

export default function Perfmery({ setHeader }) {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [currentLocaton, setCurrentLocation] = useState(perfumeriesData[slug]);
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = currentLocaton
    ? `${t(currentLocaton.title)} - ${SITE_NAME}`
    : `${SITE_NAME}`;
  const pageDescription = currentLocaton
    ? `${currentLocaton.name} - ${t(currentLocaton.tagline)}`
    : t("header.locations.metaDescription");
  const ogImage = currentLocaton?.heroImg
    ? currentLocaton.heroImg.startsWith("http")
      ? currentLocaton.heroImg
      : `${SITE_URL}${currentLocaton.heroImg}`
    : DEFAULT_OG_IMAGE;
  const enUrl = `${SITE_URL}/en/perfumeries/${slug}`;
  const meUrl = `${SITE_URL}/me/parfimerije/${slug}`;
  const breadcrumbItems = [
    { name: SITE_NAME, item: `${SITE_URL}/${pageLang}` },
    {
      name: t("header.locations.pageTitle"),
      item:
        pageLang === "en"
          ? `${SITE_URL}/en/perfumeries`
          : `${SITE_URL}/me/parfimerije`,
    },
    currentLocaton ? { name: currentLocaton.name, item: pageUrl } : null,
  ].filter(Boolean);
  const localBusinessJsonLd = currentLocaton
    ? {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: currentLocaton.name,
        url: pageUrl,
        image: ogImage,
        logo: currentLocaton.logo
          ? `${SITE_URL}${currentLocaton.logo}`
          : SITE_LOGO,
        description: pageDescription,
      }
    : null;

  useEffect(() => {
    setCurrentLocation(perfumeriesData[slug]);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang }}>
        {/* <!-- meta tag --> */}
        <meta charset="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" href={enUrl} hreflang="en" />
        <link rel="alternate" href={meUrl} hreflang="me" />
        <link rel="alternate" href={enUrl} hreflang="x-default" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:image" content={ogImage} />
      </Helmet>
      <Loader />
      {/* // <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div
          className="rs-breadcrumbs electimuss-3"
          style={{
            backgroundImage: `url(${
              currentLocaton.heroImg === ""
                ? "/assets/images/breadcrumbs/inr-2.jpg"
                : currentLocaton.heroImg
            })`,
          }}
        >
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {currentLocaton.title}
                {/*<span className="watermark">{currentLocaton.title}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <Breadcrumbs />
        {/* <!-- Breadcrumbs End --> */}

        {/* <!-- About Section Start --> */}
        <div className="rs-about style1 lion-bg pt-20 pb-40 md-pt-20 md-pb-20">
          <div className="container">
            <div className="row y-middle">
              <div className="col-lg-4 pr-15 md-pr-15 md-mb-50">
                <div className="brand-logo">
                  <img src={currentLocaton.logo} alt={currentLocaton.title} />
                </div>
              </div>
              <div className="col-lg-8 pl-45 md-pl-15">
                {currentLocaton.lead === "" ? (
                  <p className="desc margin-0 pb-15">Coming soon...</p>
                ) : (
                  parse(t(currentLocaton.lead))
                )}
              </div>
            </div>
            {currentLocaton.story === "" ? null : (
              <div className="row y-middle pt-60 pb-40 md-pt-60 md-pb-20">
                <div className="col-lg-6 md-mb-50">
                  <div className="sec-title mb-60">
                    <span className="sub-text">
                      {t("locations.pageHeadings.location")}
                    </span>
                    <h2 className="title pb-22">
                      {currentLocaton.name} <br />
                      {t(currentLocaton.tagline)}
                    </h2>
                    <div className="heading-border-line left-style"></div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      {parse(t(currentLocaton.longStory1))}
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 pl-50 md-pl-15">
                <div className="rs-videos choose-video">
                  <div className="images-video">
                  <img src={currentLocaton.storyImg} alt={currentLocaton.name} />
                  </div>
                  {currentLocaton.showVideo ? (
                    <div className="animate-border">
                      <a
                        className="popup-border"
                        href={currentLocaton.videoUrl}
                      >
                        <i className="fa fa-play"></i>
                      </a>
                    </div>
                  ) : null}
                </div>
                </div>
              </div>
            )}
            {currentLocaton.story === "" ? null : (
              <div className="row y-middle pt-60 pb-40 md-pt-60 md-pb-20">
                <div className="col-lg-6 pe-50 md-pr-15">
                  <div className="images-part">
                    <img src={currentLocaton.storyImg2} alt={currentLocaton.name} />
                  </div>
                </div>
                <div className="col-lg-6 md-mb-50">
                  <div className="sec-title mb-60">
                    <span className="sub-text">
                    {t(currentLocaton.smallHeading)}
                    </span>
                    <h2 className="title pb-22">
                      {t(currentLocaton.title1)}
                    </h2>
                    <div className="heading-border-line left-style"></div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      {parse(t(currentLocaton.longStory2))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* <!-- About Section End --> */}

        {/* <!-- Gallery Section Start --> */}
        <div className="rs-team style2 lion-bg pt-20 pb-40 md-pt-20 md-pb-20">
          <div className="container">
            <div className="row y-middle">
            <div className="sec-title text-center mb-60">
                <span className="sub-text">
                  {t("locations.pageHeadings.gallery")}
                </span>
                <div className="heading-border-line"></div>
                <div className="col-lg-12 mt-50 pr-15 md-pr-15 md-mb-50">
                  <GalleryMini
                />
              </div>
            </div>
            </div>
          </div>
        </div>
        {/* <!-- Gallery Section End --> */}

        {/* <!-- Newsletter section start --> */}
        <div className="rs-newsletter lion-bg style1 pt-90 md-pt-60 mb--110">
          <Newsletter />
        </div>

        {/* <!-- Newsletter section end --> */}
      </div>
      {/* // <!-- Main content End --> */}
      {localBusinessJsonLd ? (
        <StructuredData
          data={[
            breadcrumbJsonLd(breadcrumbItems),
            localBusinessJsonLd,
          ]}
        />
      ) : null}
    </>
  );
}
